import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import LogoLockup from "@/components/landing/Logo";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";

export default function Navbar() {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    const LINKS = [
        { href: "#how-to-play", label: t("nav.howToPlay") },
        { href: "#play-modes", label: t("nav.modes") },
        { href: "#powerups", label: t("nav.loadout") },
        { href: "#betting", label: t("nav.wagersAndBets") },
        { href: "#coins", label: t("nav.coins") },
        { href: "#zero-out", label: t("nav.pairTrick") },
        { href: "#faq", label: t("nav.faq") },
        { to: "/privacy", label: t("nav.privacy"), isRoute: true },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`sticky top-0 z-50 w-full transition-colors duration-200 ${
                scrolled
                    ? "backdrop-blur-md bg-[rgba(251,247,238,0.85)] border-b border-[hsl(var(--border))]"
                    : "bg-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
                <a
                    href="#top"
                    className="flex items-center gap-2 flex-shrink-0"
                    aria-label="Golf Power Play - home"
                >
                    <LogoLockup size={34} />
                </a>

                <nav className="hidden xl:flex items-center gap-5">
                    {LINKS.map((l) =>
                        l.isRoute ? (
                            <Link
                                key={l.to}
                                to={l.to}
                                className="text-sm font-medium text-[#1B2621] hover:text-[#0B4A34] transition-colors whitespace-nowrap"
                                data-testid={`nav-link-${l.to.replace("/", "")}`}
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.href}
                                href={l.href}
                                className="text-sm font-medium text-[#1B2621] hover:text-[#0B4A34] transition-colors whitespace-nowrap"
                                data-testid={`nav-link-${l.href.replace("#", "")}`}
                            >
                                {l.label}
                            </a>
                        )
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                    <LanguageSwitcher />
                    <Button
                        asChild
                        className="bg-[#C8A24A] text-[#0B0F0D] hover:bg-[#E0C06A] h-10 px-5 rounded-xl font-semibold"
                        data-testid="nav-notify-button"
                    >
                        <a href="#waitlist">{t("nav.notifyMe")}</a>
                    </Button>
                </div>

                <div className="lg:hidden flex items-center gap-1">
                    <LanguageSwitcher compact />
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("nav.openMenu")}
                                data-testid="mobile-menu-button"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-[#FBF7EE] w-72">
                            <SheetHeader>
                                <SheetTitle>
                                    <LogoLockup size={32} />
                                </SheetTitle>
                                <SheetDescription className="sr-only">
                                    {t("nav.mobileMenuDesc")}
                                </SheetDescription>
                            </SheetHeader>
                            <div className="mt-6 flex flex-col gap-1">
                                {LINKS.map((l) =>
                                    l.isRoute ? (
                                        <Link
                                            key={l.to}
                                            to={l.to}
                                            onClick={() => setOpen(false)}
                                            className="py-3 px-3 rounded-lg text-base font-medium text-[#1B2621] hover:bg-[rgba(26,154,107,0.08)] transition-colors"
                                            data-testid={`mobile-nav-link-${l.to.replace("/", "")}`}
                                        >
                                            {l.label}
                                        </Link>
                                    ) : (
                                        <a
                                            key={l.href}
                                            href={l.href}
                                            onClick={() => setOpen(false)}
                                            className="py-3 px-3 rounded-lg text-base font-medium text-[#1B2621] hover:bg-[rgba(26,154,107,0.08)] transition-colors"
                                        >
                                            {l.label}
                                        </a>
                                    )
                                )}
                                <Button
                                    asChild
                                    className="mt-4 bg-[#C8A24A] text-[#0B0F0D] hover:bg-[#E0C06A] h-11 rounded-xl font-semibold"
                                >
                                    <a
                                        href="#waitlist"
                                        onClick={() => setOpen(false)}
                                    >
                                        {t("nav.notifyMe")}
                                    </a>
                                </Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>

                <div className="hidden md:flex lg:hidden items-center">
                    <LanguageSwitcher compact />
                </div>
            </div>
        </header>
    );
}
